import { ChangeEvent } from 'react';
import { Box, Card } from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { useAutoFocus } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { MetaplanFormProps } from '../MetaplanForm';

interface NameCardProps
  extends Pick<MetaplanFormProps, 'values' | 'onChange' | 'errors'> {
  disabled?: boolean;
  metaplanId?: string;
}

// Regex to allow only Latin-1 characters (includes basic letters, digits, punctuation, and symbols)
const VALID_NAME_REGEX = /^[\u0020-\u007E\u00A0-\u00FF]*$/;

export function NameCard({
  values: { name },
  errors,
  disabled = false,
  onChange,
}: Readonly<NameCardProps>) {
  const t = useTranslationCatalog();

  const { ref } = useAutoFocus();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (VALID_NAME_REGEX.test(newValue) || newValue === '') {
      onChange(e);
    }
  };

  const errorMessage = errors?.name;
  const hasError = !!errorMessage;

  return (
    <Card>
      <Card.Header title={t('subscriptions.form.planName.title')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3" ref={ref}>
          <FormField.Input
            name="name"
            value={name}
            placeholder={t('subscriptions.form.planName.placeholder')}
            onChange={handleChange}
            showHelpText
            helpText={
              errorMessage
                ? t(errorMessage)
                : t('subscriptions.form.planName.helpText')
            }
            helpIcon={hasError ? ExclamationCircleIcon : undefined}
            appearance={hasError ? 'danger' : undefined}
            disabled={disabled}
          />
        </Box>
      </Card.Body>
    </Card>
  );
}
